import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Flame, Trophy, Bookmark, Target, Zap, BookOpen, Radar as RadarIcon, TrendingUp, Play } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const { user, loading, loginWithGoogle } = useAuth();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [topics, setTopics] = useState({});
  const [activeClass, setActiveClass] = useState(null);
  const [progress, setProgress] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/leaderboard").then((r) => setBoard(r.data));
    api.get("/bookmarks").then((r) => setBookmarks(r.data));
    api.get("/practice-attempts/mine").then((r) => setProgress(r.data)).catch(() => setProgress({ best_per_chapter: [], recent: [], class_summary: [], total_attempts: 0 }));
    api.get("/topic-stats").then((r) => {
      setTopics(r.data);
      const first = Object.keys(r.data)[0];
      if (first) setActiveClass(first);
    });
  }, [user, loading, nav]);

  if (!loading && !user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center gap-4">
        <div className="text-xs uppercase tracking-[0.2em] text-pink-300">Dashboard</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Sign in to see your progress</h1>
        <p className="text-zinc-400 max-w-md">Streaks, quiz history, bookmarks and a topic-level radar of your accuracy — all synced to your Google account.</p>
        <button data-testid="dashboard-signin-btn" onClick={loginWithGoogle} className="mt-2 bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold px-6 py-2">
          Continue with Google
        </button>
      </div>
    );
  }

  if (!data) return <div className="text-zinc-500">Loading dashboard…</div>;

  const classIds = Object.keys(topics);
  const radarData = activeClass && topics[activeClass] ? topics[activeClass].map((t) => ({ topic: t.topic, accuracy: t.accuracy })) : [];

  return (
    <div>
      <div className="mb-8 fade-up flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-white/10" />}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1">Hello, {data.user.name || "student"}</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Your board-prep dashboard</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Flame className="w-5 h-5 text-orange-400" />
          <span className="gradient-streak font-display text-2xl font-bold">{data.user.streak}</span>
          <span className="text-zinc-500 text-xs uppercase tracking-wider">day streak</span>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-4">
        <div className="md:col-span-4 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up" data-testid="stat-attempts">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-pink-300 mb-2"><Target className="w-3.5 h-3.5" /> Quiz attempts</div>
          <div className="font-display text-4xl font-bold">{data.stats.quiz_attempts}</div>
        </div>
        <div className="md:col-span-4 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up fade-up-1" data-testid="stat-accuracy">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-pink-300 mb-2"><Trophy className="w-3.5 h-3.5" /> Avg accuracy</div>
          <div className="font-display text-4xl font-bold">{data.stats.avg_accuracy}%</div>
        </div>
        <div className="md:col-span-4 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up fade-up-2" data-testid="stat-mental">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-pink-300 mb-2"><Zap className="w-3.5 h-3.5" /> Mental sessions</div>
          <div className="font-display text-4xl font-bold">{data.stats.mental_sessions}</div>
        </div>

        {/* Radar chart */}
        <div className="md:col-span-7 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up" data-testid="topic-radar">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-pink-300 flex items-center gap-2"><RadarIcon className="w-3.5 h-3.5" /> Topic accuracy radar</div>
            <div className="flex gap-1.5">
              {classIds.map((c) => (
                <button
                  key={c}
                  data-testid={`radar-class-${c}`}
                  onClick={() => setActiveClass(c)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${
                    activeClass === c ? "bg-sky-400 text-slate-900 border-yellow-400 font-semibold" : "border-white/10 text-zinc-500"
                  }`}
                >
                  Class {c}
                </button>
              ))}
            </div>
          </div>
          {radarData.length >= 3 ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis dataKey="topic" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} />
                  <Radar name="Accuracy" dataKey="accuracy" stroke="#FACC15" fill="#FACC15" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-zinc-500 py-10 text-center">
              Take at least a couple of quizzes across topics to see your radar. <Link to="/quiz" className="text-yellow-300 hover:underline">Start a quiz</Link>.
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="md:col-span-5 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up fade-up-1" data-testid="leaderboard">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-4 flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Weekly leaderboard</div>
          {board.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">Be the first this week!</div>
          ) : (
            <div className="space-y-1.5">
              {board.slice(0, 8).map((row, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex gap-3 items-center">
                    <div className="font-mono text-zinc-500 w-6">#{i + 1}</div>
                    {row.picture ? <img src={row.picture} alt="" className="w-5 h-5 rounded-full" /> : null}
                    <div className="text-zinc-200">{row.name}</div>
                  </div>
                  <div className="font-mono text-yellow-300">{row.score}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-7 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up fade-up-2" data-testid="recent-quizzes">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-4 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Recent quizzes</div>
          {data.recent_quizzes.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No attempts yet. <Link to="/quiz" className="text-yellow-300 hover:underline">Take today's quiz</Link>.</div>
          ) : (
            <div className="space-y-2">
              {data.recent_quizzes.map((q) => (
                <div key={q.id || q.created_at} className="flex items-center justify-between border border-white/5 rounded-md px-3 py-2 text-sm">
                  <div className="flex gap-3 items-center">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 w-16">Class {q.class_level}</div>
                    <div className="text-zinc-300">{q.slot}</div>
                    <div className="text-zinc-500 font-mono text-xs">{q.date}</div>
                  </div>
                  <div className="font-mono text-yellow-300">{q.score}/{q.total}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-5 rounded-lg border border-white/10 bg-[#152040] p-5 fade-up fade-up-2" data-testid="bookmarks-panel">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-4 flex items-center gap-2"><Bookmark className="w-3.5 h-3.5" /> Bookmarks</div>
          {bookmarks.length === 0 ? (
            <div className="text-sm text-zinc-500 py-4">No bookmarks yet — tap the bookmark icon on any exercise question.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {bookmarks.map((b) => (
                <Link key={b.id} to={`/chapters/${b.chapter_id}`} className="block rounded-md border border-white/10 bg-[#0b1428] p-3 text-sm hover:border-orange-300/50">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Class {b.class_level} · {b.chapter_id}</div>
                  <div className="text-zinc-200 line-clamp-2">{b.question_text}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Chapter Progress — practice test best scores */}
        <div className="md:col-span-12 rounded-lg border border-white/10 bg-gradient-to-br from-pink-300/[0.04] via-transparent to-orange-300/[0.04] p-5 fade-up fade-up-3" data-testid="chapter-progress">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-[11px] uppercase tracking-wider text-pink-300 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" /> Chapter progress
              {progress?.total_attempts > 0 && (
                <span className="text-zinc-500 normal-case tracking-normal">· {progress.total_attempts} attempt{progress.total_attempts === 1 ? "" : "s"} recorded</span>
              )}
            </div>
            {progress?.class_summary?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {progress.class_summary.map((s) => (
                  <div key={s.class_id} className="text-[11px] rounded-full border border-white/10 bg-[#0b1428] px-3 py-1 text-zinc-300">
                    <span className="text-pink-300">{s.class_label}:</span> {s.chapters_practised} ch · {s.avg_accuracy}%
                  </div>
                ))}
              </div>
            )}
          </div>
          {!progress || progress.best_per_chapter.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="progress-empty">
              No practice tests yet. <Link to="/classes" className="text-orange-300 hover:underline">Pick a chapter</Link> and take a 10-question mock — your best score will appear here.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {progress.best_per_chapter.map((c) => (
                <Link
                  key={c.chapter_id}
                  to={`/practice/${c.chapter_id}`}
                  className="group rounded-lg border border-white/10 bg-[#0b1428] p-4 hover:border-orange-300/40 transition-colors"
                  data-testid={`progress-${c.chapter_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-pink-300">{c.class_label} · Ch {c.chapter_number}</div>
                      <div className="font-display text-sm font-semibold text-zinc-100 truncate group-hover:text-orange-200 transition-colors">{c.chapter_title}</div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="font-display text-2xl font-bold gradient-sunset-text leading-none">{Math.round(c.best_accuracy)}%</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{c.best_score}/{c.total_questions}</div>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300" style={{ width: `${Math.min(100, c.best_accuracy)}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
                    <span>{c.attempts} attempt{c.attempts === 1 ? "" : "s"}</span>
                    <span className="inline-flex items-center gap-1 text-orange-300 group-hover:text-yellow-300"><Play className="w-3 h-3" /> Retake</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
